import { Icon } from 'global/components/micros/Icon';
import { Button } from 'global/components/micros/Button';
import Skeleton from 'global/components/micros/Layout/Skeleton';
import sanitizeHtml from 'sanitize-html';
import RichText from 'global/components/micros/RichText/RichText';
import { useEffect, useRef } from 'react';

import * as S from './styles';

import { formatGtm } from '../../../utils/functions';

import { DialogProps } from './types';

export const Dialog = ({
  iconRef,
  altIcon,
  iconCloseRef,
  alticonClose,
  preTitle,
  title,
  subTitle,
  subTitle2,
  list,
  text,
  note,
  buttonContainerLayout,
  buttonsSize,
  buttonGhost,
  buttonPrimary,
  buttonSecondary,
  appleStoreButtonHref,
  playStoreButtonHref,
  appleStoreImg,
  appleStoreAlt,
  playStoreImg,
  playStoreAlt,
  mode,
  variant,
  gtmName,
  openDialog,
  handleClose,
}: DialogProps) => {
  const isInitialMount = useRef(true);
  const dataLayer = window.dataLayer || [];
  useEffect(() => {
    if (isInitialMount.current) {
      // Skip effect on initial mount
      isInitialMount.current = false;
      return;
    }

    dataLayer.push({
      event: 'dialog',
      action: openDialog ? 'open' : 'close',
      name: formatGtm(title),
    });
  }, [openDialog]);

  return (
    <>
      {openDialog && (
        <S.Overlay>
          <S.Container
            mode={mode}
            variant={variant}
            data-testid="Dialog"
            buttonContainerLayout={buttonContainerLayout}
          >
            {title ? (
              <S.Dialog data-testid="DialogItem">
                <S.HeaderContainer>
                  {(iconRef || iconCloseRef) && (
                    <S.Icons>
                      <S.Iconref>
                        {iconRef && (
                          <Icon
                            icon={iconRef}
                            size="big"
                            alt={altIcon}
                            data-testid="iconCard"
                          />
                        )}
                      </S.Iconref>
                      <S.IconClose
                        onClick={() => {
                          handleClose();
                        }}
                      >
                        {iconCloseRef && (
                          <Icon
                            icon={iconCloseRef}
                            size="medium"
                            alt={alticonClose}
                            data-testid="iconCard"
                          />
                        )}
                      </S.IconClose>
                    </S.Icons>
                  )}

                  {preTitle && (
                    <S.PreTitle aria-label={preTitle}>{preTitle}</S.PreTitle>
                  )}

                  {title && <S.Title aria-label={title}>{title}</S.Title>}

                  {subTitle && (
                    <S.SubTitle aria-label={subTitle}>{subTitle}</S.SubTitle>
                  )}

                  {subTitle2 && (
                    <S.SubTitle2>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(subTitle2),
                        }}
                      />
                    </S.SubTitle2>
                  )}

                  {list?.length && (
                    <S.ListContainer data-testid="ListContainer">
                      {list.map((listItem, index) => (
                        <S.ListItem key={index}>
                          {listItem.icon && (
                            <Icon icon={listItem.icon} size="medium" />
                          )}
                          <S.Text>{listItem.text}</S.Text>
                        </S.ListItem>
                      ))}
                    </S.ListContainer>
                  )}

                  {note && <S.Note aria-label={note}>{note}</S.Note>}
                </S.HeaderContainer>

                {text && (
                  <S.ContextContainer data-testid="ContextContainer">
                    <S.ScrollText aria-label={text}>
                      <RichText text={text || ''} />
                    </S.ScrollText>
                  </S.ContextContainer>
                )}

                {buttonPrimary?.text && (
                  <S.FooterContainer data-testid="ButtonsContainer">
                    <S.ButtonsContainer>
                      {buttonGhost && (
                        <S.ButtonGhost>
                          <Button
                            onClick={buttonPrimary.onClick}
                            styles="ghost"
                            aria-label={buttonGhost.ariaLabel!}
                            size={buttonsSize}
                            variant="insurance"
                            data-gtm-type="click"
                            data-gtm-clicktype="button"
                            data-gtm-name={gtmName}
                            data-gtm-subname={`${formatGtm(title)}:${formatGtm(
                              buttonGhost.text
                            )}`}
                            data-testid={`button-ghost-${formatGtm(
                              buttonGhost.text
                            )}`}
                          >
                            {buttonGhost.text}
                          </Button>
                        </S.ButtonGhost>
                      )}

                      {buttonSecondary && (
                        <S.ButtonSecondary>
                          <Button
                            onClick={buttonSecondary.onClick}
                            styles="secondary"
                            aria-label={buttonSecondary.ariaLabel!}
                            size={buttonsSize}
                            variant={
                              buttonSecondary.variant
                                ? buttonSecondary.variant
                                : 'insurance'
                            }
                            data-gtm-type="click"
                            data-gtm-clicktype="button"
                            data-gtm-name={gtmName}
                            data-gtm-subname={`${formatGtm(title)}:${formatGtm(
                              buttonSecondary.text
                            )}`}
                            data-testid={`button-secondary-${formatGtm(
                              buttonSecondary.text
                            )}`}
                          >
                            {buttonSecondary.text}
                          </Button>
                        </S.ButtonSecondary>
                      )}

                      {buttonPrimary && (
                        <S.ButtonPrimary>
                          <Button
                            onClick={buttonPrimary.onClick}
                            styles="primary"
                            aria-label={buttonPrimary.ariaLabel!}
                            size={buttonsSize}
                            variant={
                              buttonPrimary.variant
                                ? buttonPrimary.variant
                                : 'insurance'
                            }
                            data-gtm-type="click"
                            data-gtm-clicktype="button"
                            data-gtm-name={gtmName}
                            data-gtm-subname={`${formatGtm(title)}:${formatGtm(
                              buttonPrimary.text
                            )}`}
                            data-testid={`button-primary-${formatGtm(
                              buttonPrimary.text
                            )}`}
                          >
                            {buttonPrimary.text}
                          </Button>
                        </S.ButtonPrimary>
                      )}
                    </S.ButtonsContainer>
                  </S.FooterContainer>
                )}

                {appleStoreButtonHref && playStoreButtonHref && (
                  <S.FooterContainer>
                    <S.StoreButtonsContainer data-testid="FooterContainer">
                      <S.StoreButton
                        href={appleStoreButtonHref}
                        target="_blank"
                      >
                        <S.StoreImg src={appleStoreImg} alt={appleStoreAlt} />
                      </S.StoreButton>
                      <S.StoreButton href={playStoreButtonHref} target="_blank">
                        <S.StoreImg src={playStoreImg} alt={playStoreAlt} />
                      </S.StoreButton>
                    </S.StoreButtonsContainer>
                  </S.FooterContainer>
                )}
              </S.Dialog>
            ) : (
              <S.Dialog data-testid="shimmer">
                <Skeleton width={100} height={11} borderRadius={1.6}></Skeleton>
                <Skeleton width={100} height={22} borderRadius={0.4}></Skeleton>

                <S.ButtonsContainer>
                  <Skeleton
                    width={100}
                    height={4.8}
                    borderRadius={0.4}
                  ></Skeleton>
                  <Skeleton
                    width={100}
                    height={4.8}
                    borderRadius={0.4}
                  ></Skeleton>
                  <Skeleton
                    width={100}
                    height={4.8}
                    borderRadius={0.4}
                  ></Skeleton>
                </S.ButtonsContainer>
              </S.Dialog>
            )}
          </S.Container>
        </S.Overlay>
      )}
    </>
  );
};
